import { InfoCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Select, Skeleton, Table, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteScribeDotPhraseOverride } from 'src/modules/ambient_solo/store/actions/settings';
import ReactQuill from 'react-quill';
import { listPractitionersFromOrg } from 'src/store/actions/practitioner.action';
import { classNames } from 'src/utilities';
import { listDotPhraseOverrides } from '../../store/actions/settings';

const DotPhraseAdminSource = ({
  setSelectedDotPhrasesDataSource,
  selectedDotPhrasesDataSource,
  refreshTrigger,
}) => {
  const dispatch = useDispatch();

  const [organizationDropdown, setOrganizationDropdown] = useState([]);
  const [practitionerDropdown, setPractitionerDropdown] = useState([]);
  const [dotPhraseDropdown, setDotPhraseDropdown] = useState([]);
  const [selectedPractitioners, setSelectedPractitioners] = useState([]);
  const [selectedDotPhrases, setSelectedDotPhrases] = useState([]);

  const columns = [
    {
      title: 'Dot phrase',
      render: (text, record) => (
        <div className="flex justify-between items-center">
          <span>{record.title}</span>
          <div className="flex justify-end gap-2">
            <Tooltip title="Delete dot phrase">
              <DeleteOutlined
                className="text-red-500 text-md cursor-pointer"
                onClick={() => {
                  dispatch(
                    deleteScribeDotPhraseOverride(
                      record?.dot_phrase_overrides_id,
                      () => {
                        setSelectedDotPhrasesDataSource(
                          selectedDotPhrasesDataSource.filter(
                            (item) =>
                              item.dot_phrase_overrides_id !==
                              record?.dot_phrase_overrides_id
                          )
                        );
                        setSelectedDotPhrases(
                          selectedDotPhrases.filter(
                            (item) => item !== record?.dot_phrase_overrides_id
                          )
                        );
                      }
                    )
                  );
                }}
              />
            </Tooltip>
            <Tooltip
              title={
                <div className="quill-view-only" style={{ width: '500px' }}>
                  <ReactQuill
                    value={(record?.template || '')
                      ?.replaceAll('\n', '<br />')
                      .replaceAll('\\n', '<br />')}
                    className="organization bg-white border border-[#00d090] rounded"
                    readOnly={true}
                    theme="snow"
                    modules={{
                      toolbar: false,
                    }}
                  />
                </div>
              }
              placement={'right'}
              arrow={false}
              overlayStyle={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                overflow: 'auto',
              }}
            >
              <span style={{ cursor: 'default' }} className="text-gray-400">
                <InfoCircleOutlined />
              </span>
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  const {
    allPractitionerNames,
    allPractitionerNamesLoading,
    currentPractitioner,
  } = useSelector((state) => state?.practitionerState);

  const { dotPhraseOverridesList, dotPhraseOverridesDeleteLoading } =
    useSelector((state) => state?.settingsState);

  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let orgData = [];
      getActiveOrganizationsList.forEach((key, index) => {
        orgData.push({
          label: key?.org_name,
          value: key?.org_uuid,
        });
      });
      setOrganizationDropdown(orgData);
    }
  }, [getActiveOrganizationsList]);

  useEffect(() => {
    dispatch(listPractitionersFromOrg(currentPractitioner?.org_uuid, false));
    dispatch(
      listDotPhraseOverrides({
        overrides: true,
      })
    );
  }, [dispatch, refreshTrigger]);

  useEffect(() => {
    if (dotPhraseOverridesList.length > 0) {
      const dotPhrases =
        selectedPractitioners.length > 0
          ? dotPhraseOverridesList.filter((item) =>
              selectedPractitioners.includes(item.practitioner_id)
            )
          : dotPhraseOverridesList;

      setDotPhraseDropdown(
        dotPhrases.map((item) => ({
          label: item.title,
          value: item.dot_phrase_overrides_id,
          body:
            typeof item.template === 'object'
              ? JSON.stringify(item.template)
              : item.template,
        }))
      );
    } else {
      setDotPhraseDropdown([]);
    }
  }, [dotPhraseOverridesList, selectedPractitioners]);

  useEffect(() => {
    if (allPractitionerNamesLoading === false) {
      setPractitionerDropdown(
        allPractitionerNames.map((item) => ({
          label: item.practitioner_name,
          value: item.practitioner_id,
        }))
      );
    }
  }, [allPractitionerNames, allPractitionerNamesLoading]);

  useEffect(() => {
    if (selectedDotPhrases.length > 0) {
      setSelectedDotPhrasesDataSource(
        dotPhraseOverridesList
          .filter((item) =>
            selectedDotPhrases.includes(item.dot_phrase_overrides_id)
          )
          .map((item) => ({
            ...item,
            key: item.dot_phrase_overrides_id,
          }))
      );
    } else {
      setSelectedDotPhrasesDataSource([]);
    }
  }, [dotPhraseOverridesList, selectedDotPhrases, refreshTrigger]);

  return (
    <div className="flex-1">
      <div className="flex flex-col gap-1">
        <div className="text-lg font-bold font-sans pl-1 mb-2">Source</div>
        <Select
          mode="multiple"
          showSearch={true}
          optionFilterProp="label"
          style={{ width: '100%' }}
          options={practitionerDropdown}
          placeholder="Select practitioner(s)"
          value={selectedPractitioners}
          onChange={(values) => {
            setSelectedPractitioners(values);
            if (dotPhraseOverridesList.length > 0) {
              const filteredDotPhrases = dotPhraseOverridesList.filter((item) =>
                values.includes(item.practitioner_id)
              );
              setDotPhraseDropdown(
                filteredDotPhrases.map((item) => ({
                  label: item.title,
                  value: item.dot_phrase_overrides_id,
                  body:
                    typeof item.template === 'object'
                      ? JSON.stringify(item.template)
                      : item.template,
                }))
              );
            }
          }}
          className={classNames('font-sans w-full', 'single-select')}
        />
        <Select
          mode="multiple"
          showSearch={true}
          optionFilterProp="label"
          style={{ width: '100%' }}
          options={dotPhraseDropdown}
          placeholder="Select dot phrase(s)"
          value={selectedDotPhrases}
          onChange={(values) => setSelectedDotPhrases(values)}
          className={classNames('font-sans w-full', 'single-select')}
          listHeight={300}
          optionRender={(option) => (
            <div className="flex items-center gap-2">
              <span>{option.data.label}</span>
              <Tooltip
                title={
                  <div className="quill-view-only" style={{ width: '500px' }}>
                    <ReactQuill
                      value={(option.data.body || '')?.replaceAll(
                        '\n',
                        '<br />'
                      )}
                      className="organization bg-white border-2 border-[#00d090] rounded"
                      readOnly={true}
                      theme="snow"
                      modules={{ toolbar: false }}
                    />
                  </div>
                }
                placement={'right'}
                arrow={false}
                overlayStyle={{
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  overflow: 'auto',
                }}
              >
                <span style={{ cursor: 'default' }} className="text-gray-400">
                  <InfoCircleOutlined />
                </span>
              </Tooltip>
            </div>
          )}
        />
        {selectedDotPhrases.length > 0 &&
          selectedDotPhrasesDataSource.length > 0 && (
            <div className="mt-2">
              <Button
                type="primary"
                onClick={() => {
                  setSelectedDotPhrasesDataSource([]);
                  setSelectedDotPhrases([]);
                }}
              >
                Clear
              </Button>
              <div className="mt-2">
                <Table
                  columns={columns}
                  pagination={false}
                  bordered={true}
                  dataSource={selectedDotPhrasesDataSource}
                  loading={dotPhraseOverridesDeleteLoading}
                />
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default DotPhraseAdminSource;
